import {
  CircularProgress,
  Popover,
  Theme,
  Typography,
  WithStyles,
  withStyles
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { TFunction } from 'i18next'
import CalendarIcon from 'mdi-react/CalendarBlankOutlineIcon'
import EditIcon from 'mdi-react/TuneIcon'
import moment from 'moment'
import { Fragment, useState } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { formValueSelector } from 'redux-form'
import { navigate } from '../../../actions/routes'
import { Navigate, OpenDialog } from '../../../actions/types'
import { selectApartmentById } from '../../../selectors/apartments'
import { selectApplicationsByApartmentId } from '../../../selectors/applications'
import { selectContentIdFromPathname } from '../../../selectors/router'
import { MuiClasses } from '../../../types'
import { Apartment } from '../../../types/apartments'
import { Application } from '../../../types/applications'
import {
  FormShowing,
  ReservableShowing,
  Showing
} from '../../../types/showings'
import { StateInterface } from '../../../types/states'

const styles = ({ palette, spacing }: Theme) => ({
  showingCard: {
    display: 'flex',
    height: '70px',
    backgroundColor: palette.grey[200],
    marginBottom: '1rem',
    alignItems: 'center',
    borderRadius: '7px',
    justifyContent: 'space-between',
    padding: '10px',
    '&:last-child': {
      marginBottom: '0'
    }
  },
  infoContainer: {
    display: 'flex'
  },
  iconContainer: {
    backgroundColor: '#E2EBFD',
    borderRadius: '7px',
    padding: '0.6rem 0.7rem',
    color: palette.primary.contrastText,
    margin: '0.7rem'
  },
  participants: {
    alignSelf: 'flex-start',
    justifySelf: 'flex-end',
    backgroundColor: '#E2EBFD',
    borderRadius: '4px',
    height: '1.3rem',
    width: '1.3rem',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0.3rem'
  },
  participantsForDelete: {
    alignSelf: 'flex-start',
    justifySelf: 'flex-end',
    backgroundColor: '#F41211',
    borderRadius: '4px',
    height: '1.3rem',
    width: '1.3rem',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0.5rem 0.3rem 0.3rem 0.3rem'
  },
  popover: {
    pointerEvents: 'none' as const
  },
  paper: {
    padding: spacing(1)
  },
  edit: {
    display: 'flex',
    alignSelf: 'flex-start',
    justifyContent: 'center',
    margin: '0.3rem',
    fontSize: '0.7rem'
  },
  editText: {
    margin: 'auto',
    marginRight: '4px'
  },
  datetime: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignSelf: 'center',
    'text-transform': 'capitalize'
  },
  deleteIcon: {
    cursor: 'pointer',
    width: '15px'
  }
})

interface ShowingCardProps extends WithStyles {
  showing?: Showing | ReservableShowing
  address?: string
  cardDate?: string
  handleOnClick?(arg: string): void
  t: TFunction
  openDialog?: OpenDialog
  setCurrentShowing?(arg: any): void
  classes: MuiClasses
  useCase: 'count' | 'edit' | 'empty' | 'delete'
  isShowingDeleting?: boolean
}

interface StateProps {
  apartmentId: any
  apartment?: Apartment
  showings?: FormShowing[]
  applications?: Application[]
}

type DispatchProps = {
  navigateToPath: Navigate
}

type ComponentsProps = ShowingCardProps &
  StateProps &
  DispatchProps &
  WithStyles

const ShowingCard = ({
  openDialog,
  showing,
  address,
  handleOnClick,
  useCase,
  t,
  classes,
  applications,
  setCurrentShowing,
  cardDate,
  isShowingDeleting
}: ComponentsProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const showingId: string | undefined = showing?.showingId
  const startDate: string | undefined = showing ? showing.startDate : cardDate
  const date = moment(startDate).format('dd D.M.YYYY')
  const startTime = moment(startDate).format('HH.mm')
  const applicationIds = (showing as Showing)?.applicationIds || []
  const applicationsLength = applicationIds.length
  const invitedApplicationIds =
    (showing as Showing)?.invitedApplicationIds || []
  const invitedApplicationsLength = invitedApplicationIds.length

  const handlePopoverOpen = (event: any) => {
    setAnchorEl(event.currentTarget)
  }
  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  return (
    <Fragment>
      <div
        className={classes.showingCard}
        onClick={() => {
          handleOnClick && showingId && handleOnClick(showingId)
        }}
      >
        <div className={classes.infoContainer}>
          <div className={classes.iconContainer}>
            {isShowingDeleting ? (
              <CircularProgress
                size={24}
                style={{
                  color: '#fff'
                }}
              />
            ) : (
              <CalendarIcon />
            )}
          </div>
          <div className={classes.datetime}>
            <Typography variant='body1'>{date}</Typography>
            <Typography variant='body1'>{`${t(
              'showings.startTime'
            )}: ${startTime}`}</Typography>
            {address && <Typography>{address}</Typography>}
          </div>
        </div>
        <div>
          {useCase === 'edit' && (
            <div className={classes.edit}>
              <Typography variant='body2' className={classes.editText}>
                {t('general.cancel')}
              </Typography>
              <EditIcon style={{ width: '16px', height: '16px' }} />
            </div>
          )}
          {useCase === 'count' && (
            <Typography
              variant='body1'
              className={classes.participants}
              aria-owns={open ? 'mouse-over-popover' : undefined}
              aria-haspopup='true'
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
            >
              {applicationsLength !== 0
                ? applicationsLength
                : invitedApplicationsLength}
            </Typography>
          )}
          {useCase === 'delete' ||
            (useCase === 'count' && (
              <Typography
                variant='body1'
                className={classes.participantsForDelete}
                aria-owns={open ? 'mouse-over-popover' : undefined}
                aria-haspopup='true'
              >
                <DeleteIcon
                  className={classes.deleteIcon}
                  onClick={() => {
                    if (setCurrentShowing && openDialog && showing) {
                      setCurrentShowing(showing)
                      openDialog(
                        `confirmDeleteShowingDialog-${showing.showingId}`
                      )
                    }
                  }}
                />
              </Typography>
            ))}
          {useCase === 'empty' && <Fragment />}
        </div>
      </div>
      <Popover
        id='mouse-over-popover'
        className={classes.popover}
        classes={{
          paper: classes.paper
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        {applications &&
          applications
            .filter((application) => {
              return applicationsLength !== 0
                ? applicationIds.includes(application.applicationId)
                : invitedApplicationIds.includes(application.applicationId)
            })
            .map((application) => {
              return (
                <Typography key={application.applicationId}>
                  {application.data?.name}
                </Typography>
              )
            })}
      </Popover>
    </Fragment>
  )
}

const selector = formValueSelector('CreateShowingsForm')
const mapStateToProps = (state: StateInterface) => {
  const apartmentId = selectContentIdFromPathname(state)
  return {
    apartmentId,
    apartment: apartmentId
      ? selectApartmentById(apartmentId)(state)
      : undefined,
    applications: apartmentId
      ? selectApplicationsByApartmentId(apartmentId)(state)
      : undefined,

    showings: selector(state, 'showings')
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  navigateToPath: (path: string) => dispatch(navigate(path))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(ShowingCard))
